import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  BarChart3,
  TrendingUp,
  Eye,
  Users,
  MousePointerClick,
  Globe,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
} from 'lucide-react'
import { api } from '@/lib/api'
import { formatNumber, cn } from '@/lib/utils'

type TimeRange = '7d' | '30d' | '90d'

export function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d')

  const { data, isLoading } = useQuery({
    queryKey: ['analytics', timeRange],
    queryFn: () => api.getAnalyticsOverview(timeRange),
  })

  const stats = [
    {
      name: 'Total Pageviews',
      value: data?.overview?.totalPageviews || 0,
      change: data?.overview?.pageviewsChange || 0,
      icon: Eye,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      name: 'Unique Visitors',
      value: data?.overview?.uniqueVisitors || 0,
      change: data?.overview?.visitorsChange || 0,
      icon: Users,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
    },
    {
      name: 'Leads Captured',
      value: data?.overview?.totalLeads || 0,
      change: data?.overview?.leadsChange || 0,
      icon: MousePointerClick,
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      name: 'Conversion Rate',
      value: `${data?.overview?.conversionRate?.toFixed(1) || 0}%`,
      change: data?.overview?.conversionChange || 0,
      icon: TrendingUp,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      isPercentage: true,
    },
  ]

  // Generate chart data visualization (simple bar representation)
  const maxPageviews = Math.max(...(data?.dailyStats?.map((d: any) => d.pageviews) || [1]))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold">Analytics</h1>
          <p className="text-muted-foreground">
            Track performance across all your sites
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center gap-2 p-1 rounded-lg bg-muted">
          {[
            { value: '7d', label: '7 days' },
            { value: '30d', label: '30 days' },
            { value: '90d', label: '90 days' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setTimeRange(option.value as TimeRange)}
              className={cn(
                'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                timeRange === option.value
                  ? 'bg-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="card"
            >
              <div className="flex items-start justify-between">
                <div className={cn('p-2 rounded-lg', stat.bgColor)}>
                  <stat.icon className={cn('h-5 w-5', stat.color)} />
                </div>
                {stat.change !== 0 && (
                  <div
                    className={cn(
                      'flex items-center gap-1 text-sm font-medium',
                      stat.change > 0 ? 'text-success' : 'text-destructive'
                    )}
                  >
                    {stat.change > 0 ? (
                      <ArrowUpRight className="h-4 w-4" />
                    ) : (
                      <ArrowDownRight className="h-4 w-4" />
                    )}
                    {Math.abs(stat.change)}%
                  </div>
                )}
              </div>
              <div className="mt-4">
                <p className="text-2xl font-display font-bold">
                  {typeof stat.value === 'number'
                    ? formatNumber(stat.value)
                    : stat.value}
                </p>
                <p className="text-sm text-muted-foreground">{stat.name}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Chart Section */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Pageviews Chart */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold">Pageviews Over Time</h2>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              Last {timeRange === '7d' ? '7' : timeRange === '30d' ? '30' : '90'} days
            </div>
          </div>

          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="h-64 flex items-end gap-1">
              {data?.dailyStats?.map((day: any, i: number) => (
                <div
                  key={i}
                  className="flex-1 flex flex-col items-center gap-2"
                >
                  <div
                    className="w-full bg-primary/80 rounded-t hover:bg-primary transition-colors cursor-pointer group relative"
                    style={{
                      height: `${(day.pageviews / maxPageviews) * 200}px`,
                      minHeight: '4px',
                    }}
                  >
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-foreground text-background px-2 py-1 rounded text-xs whitespace-nowrap">
                        {formatNumber(day.pageviews)} views
                      </div>
                    </div>
                  </div>
                  {i % Math.ceil(data.dailyStats.length / 7) === 0 && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(day.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
              )) || (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </div>
          )}
        </div>

        {/* Top Sites */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Top Performing Sites</h2>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : data?.topSites?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Globe className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No site data yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {data?.topSites?.slice(0, 5).map((site: any, i: number) => (
                <div
                  key={site.id}
                  className="flex items-center gap-3"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-sm font-semibold">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{site.domainName}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatNumber(site.pageviews)} views
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-success">
                      {site.leads}
                    </p>
                    <p className="text-xs text-muted-foreground">leads</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Traffic Sources & Devices */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Traffic Sources */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Traffic Sources</h2>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-8 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {[
                { source: 'Direct', percentage: 45, color: 'bg-primary' },
                { source: 'Search', percentage: 30, color: 'bg-secondary' },
                { source: 'Social', percentage: 15, color: 'bg-accent' },
                { source: 'Referral', percentage: 10, color: 'bg-success' },
              ].map((item) => (
                <div key={item.source}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{item.source}</span>
                    <span className="text-sm text-muted-foreground">
                      {item.percentage}%
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn('h-full rounded-full', item.color)}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Devices */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Devices</h2>

          {isLoading ? (
            <div className="h-40 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="flex items-center justify-center gap-8">
              {[
                { device: 'Mobile', percentage: 55, color: 'text-primary' },
                { device: 'Desktop', percentage: 35, color: 'text-secondary' },
                { device: 'Tablet', percentage: 10, color: 'text-accent' },
              ].map((item) => (
                <div key={item.device} className="text-center">
                  <div className="relative w-20 h-20 mx-auto mb-2">
                    <svg className="w-full h-full -rotate-90">
                      <circle
                        cx="40"
                        cy="40"
                        r="36"
                        strokeWidth="8"
                        className="fill-none stroke-muted"
                      />
                      <circle
                        cx="40"
                        cy="40"
                        r="36"
                        strokeWidth="8"
                        strokeLinecap="round"
                        className={cn('fill-none', item.color.replace('text-', 'stroke-'))}
                        strokeDasharray={`${item.percentage * 2.26} 226`}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-bold">{item.percentage}%</span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{item.device}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {data?.recentEvents?.slice(0, 10).map((event: any, i: number) => (
              <div
                key={i}
                className="flex items-center gap-4 p-3 rounded-lg bg-muted/50"
              >
                <div
                  className={cn(
                    'p-2 rounded-lg',
                    event.type === 'pageview'
                      ? 'bg-primary/10 text-primary'
                      : event.type === 'lead'
                      ? 'bg-success/10 text-success'
                      : 'bg-secondary/10 text-secondary'
                  )}
                >
                  {event.type === 'pageview' ? (
                    <Eye className="h-4 w-4" />
                  ) : event.type === 'lead' ? (
                    <MousePointerClick className="h-4 w-4" />
                  ) : (
                    <BarChart3 className="h-4 w-4" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {event.type === 'pageview'
                      ? 'Page View'
                      : event.type === 'lead'
                      ? 'Lead Captured'
                      : 'Event'}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {event.domainName || event.siteId}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {new Date(event.timestamp).toLocaleTimeString()}
                </p>
              </div>
            )) || (
              <div className="text-center py-8 text-muted-foreground">
                No recent activity
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
